import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { AlertOctagon, Check, Loader2, X } from "lucide-react";
import { CallSupervisorButton } from "@/components/CallSupervisorButton";
import { EscalationChain } from "@/components/EscalationChain";

// Botón de pánico (SOS) del vigilador — incluye resolución de objetivo,
// selector manual de respaldo y banner de confirmación con acceso directo a
// "Llamar al supervisor" (build-invalidation: call-supervisor-2026-07-21).
const HOLD_MS = 1500;
// Tolerancia de movimiento del dedo mientras se mantiene presionado: en un
// celular es normal que el dedo se deslice unos pixeles sin que la persona
// quiera cancelar. Solo se cancela si el desplazamiento supera este umbral
// (deslizamiento real, no temblor/jitter).
const MOVE_CANCEL_THRESHOLD = 36;

export function PanicButton() {
  const [holding, setHolding] = useState(false);
  const [progress, setProgress] = useState(0);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [clients, setClients] = useState(null);
  const [filter, setFilter] = useState("");
  const [confirmBanner, setConfirmBanner] = useState(null);
  const intRef = useRef(null);
  const startedRef = useRef(0);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const coordsRef = useRef({ lat: null, lng: null });
  const targetNameRef = useRef(null);
  const bannerTimeoutRef = useRef(null);

  const closeBanner = () => {
    if (bannerTimeoutRef.current) { clearTimeout(bannerTimeoutRef.current); bannerTimeoutRef.current = null; }
    setConfirmBanner(null);
  };

  const vibrate = (pattern) => { try { navigator.vibrate?.(pattern); } catch {} };

  const onStart = (e) => {
    if (success || sending || selectorOpen) return;
    e.preventDefault();
    vibrate(25);
    startedRef.current = Date.now();
    setHolding(true); setProgress(0);
    intRef.current = setInterval(() => {
      const elapsed = Date.now() - startedRef.current;
      const p = Math.min(100, (elapsed / HOLD_MS) * 100);
      setProgress(p);
      if (p >= 100) {
        clearInterval(intRef.current);
        intRef.current = null;
        fire();
      }
    }, 40);
  };

  const onCancel = () => {
    if (success || sending) return;
    if (intRef.current) { clearInterval(intRef.current); intRef.current = null; }
    setHolding(false); setProgress(0);
  };

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
    onStart(e);
  };

  const onTouchMove = (e) => {
    if (!holding) return;
    const t = e.touches[0];
    const dx = t.clientX - touchStartRef.current.x;
    const dy = t.clientY - touchStartRef.current.y;
    if (Math.hypot(dx, dy) > MOVE_CANCEL_THRESHOLD) onCancel();
  };

  const finishSuccess = (data) => {
    const clientName = data.client_id ? (targetNameRef.current || null) : null;
    vibrate([90, 40, 90]);
    toast.success("SOS enviado. Mantenete a salvo.", 6000);
    setConfirmBanner({
      clientName,
      supervisorName: data.routed_supervisor_name,
      phone: data.sos_contact_phone,
    });
    if (bannerTimeoutRef.current) clearTimeout(bannerTimeoutRef.current);
    bannerTimeoutRef.current = setTimeout(() => setConfirmBanner(null), 45000);
    setSuccess(true);
    setTimeout(() => { setSuccess(false); setProgress(0); }, 2500);
  };

  const sendPanic = async (clientId) => {
    try {
      const { data } = await api.post("/panic", {
        latitude: coordsRef.current.lat, longitude: coordsRef.current.lng, client_id: clientId || null,
      });
      finishSuccess(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setSuccess(false);
    }
  };

  const openSelector = async () => {
    setSelectorOpen(true);
    if (!clients) {
      try { const { data } = await api.get("/clients"); setClients(data); }
      catch { setClients([]); }
    }
  };

  // El disparo tiene 3 pasos: 1) geolocalización, 2) preguntarle al backend si
  // puede resolver el objetivo solo (ronda activa/de hoy, servicio/visita de
  // hoy, objetivo por defecto), 3) si no puede, mostrar el selector rápido —
  // regla dura: la alerta nunca debe salir sin que se intente asignarle un
  // objetivo o, como último recurso, rutearla al supervisor de SOS por defecto.
  const fire = async () => {
    setHolding(false);
    setSending(true);
    vibrate(15);
    let lat = null, lng = null;
    if (navigator.geolocation) {
      await new Promise((res) => navigator.geolocation.getCurrentPosition(
        (pos) => { lat = pos.coords.latitude; lng = pos.coords.longitude; res(); },
        () => res(), { timeout: 5000 },
      ));
    }
    coordsRef.current = { lat, lng };
    let targetClientId = null;
    try {
      const { data } = await api.get("/panic/target-preview");
      targetClientId = data.client_id;
      targetNameRef.current = data.client_name || null;
    } catch { /* si falla el preview, el backend igual resuelve el ruteo al enviar */ }
    setSending(false);
    if (targetClientId) {
      setSending(true);
      await sendPanic(targetClientId);
      setSending(false);
    } else {
      openSelector();
    }
  };

  const pickClient = async (clientId) => {
    targetNameRef.current = clients?.find((c) => c.id === clientId)?.name || null;
    setSelectorOpen(false);
    setSending(true);
    await sendPanic(clientId);
    setSending(false);
  };

  const skipSelector = async () => {
    setSelectorOpen(false);
    setSending(true);
    await sendPanic(null);
    setSending(false);
  };

  useEffect(() => () => {
    if (intRef.current) clearInterval(intRef.current);
    if (bannerTimeoutRef.current) clearTimeout(bannerTimeoutRef.current);
  }, []);

  const filteredClients = (clients || []).filter((c) => c.name.toLowerCase().includes(filter.toLowerCase()));

  return (
    <>
      {confirmBanner && (
        <div data-testid="panic-confirm-banner" style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 200,
          background: "#16a34a", color: "white",
          padding: "calc(14px + env(safe-area-inset-top, 0px)) 16px 16px",
          boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
            <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: "0.02em" }}>
              <Check size={16} style={{ verticalAlign: "middle", marginRight: 8 }} />
              SOS ENVIADO{confirmBanner.clientName ? ` — ${confirmBanner.clientName}` : ""}. Te van a contactar, mantenete a salvo.
            </div>
            <button onClick={closeBanner} data-testid="panic-confirm-close" aria-label="Cerrar"
              style={{ background: "transparent", border: "none", color: "white", cursor: "pointer", padding: 2, flexShrink: 0 }}>
              <X size={18} />
            </button>
          </div>
          <div style={{ marginTop: 12 }}>
            <CallSupervisorButton phone={confirmBanner.phone} supervisorName={confirmBanner.supervisorName} variant="prominent" />
          </div>
          <EscalationChain variant="dark" />
        </div>
      )}

      {selectorOpen && (
        <div className="modal-overlay" data-testid="panic-target-selector">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: "#BF3437" }}>¿En qué objetivo estás?</h3>
            <p style={{ fontSize: 13 }}>No pudimos detectar el objetivo automáticamente. Elegilo para que el SOS llegue más rápido al supervisor correcto.</p>
            <input placeholder="Buscar objetivo..." value={filter} onChange={(e) => setFilter(e.target.value)}
              data-testid="panic-target-filter" style={{ marginBottom: 10 }} autoFocus />
            <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid var(--invict-bone-3)", borderRadius: 2 }}>
              {clients == null && <div style={{ padding: 16 }}><div className="skel" style={{ height: 40 }} /></div>}
              {clients && filteredClients.length === 0 && <div style={{ padding: 16, fontSize: 12, color: "var(--invict-text-muted)" }}>Sin resultados.</div>}
              {clients && filteredClients.map((c) => (
                <button key={c.id} onClick={() => pickClient(c.id)} data-testid={`panic-target-option-${c.id}`}
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 14px", border: "none", borderBottom: "1px solid var(--invict-bone-3)", background: "white", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                  {c.name}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <button type="button" className="btn-ghost" style={{ flex: 1, color: "#BF3437", borderColor: "#BF3437" }}
                onClick={skipSelector} data-testid="panic-target-skip">
                Enviar sin objetivo
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{
        position: "fixed",
        bottom: "calc(22px + env(safe-area-inset-bottom, 0px))",
        right: "calc(22px + env(safe-area-inset-right, 0px))",
        zIndex: 90, display: "flex", alignItems: "center", gap: 10,
      }}>
        {!success && !sending && !selectorOpen && (
          <div data-testid="panic-hold-hint" style={{
            background: "#0A0A0A", color: "white", padding: "6px 12px", borderRadius: 4,
            fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
            whiteSpace: "nowrap", boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
          }}>
            {holding ? "Manteniendo..." : "Mantené presionado 1,5s"}
          </div>
        )}
        <button
          onMouseDown={onStart} onMouseUp={onCancel} onMouseLeave={onCancel}
          onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onCancel} onTouchCancel={onCancel}
          disabled={sending || selectorOpen}
          style={{
            width: 84, height: 84, borderRadius: "50%", border: "none",
            background: success ? "#16a34a" : "#BF3437", color: "white",
            boxShadow: holding ? "0 0 0 10px rgba(191,52,55,0.25)" : "0 8px 24px -6px rgba(191,52,55,0.6)",
            cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            transition: "transform 0.15s ease, background 0.2s ease",
            transform: holding ? "scale(1.08)" : "scale(1)",
            animation: !holding && !success && !sending ? "pulse-red 2s infinite" : "none",
            touchAction: "none", userSelect: "none", position: "relative",
          }}
          data-testid="panic-button"
          aria-label="Botón de pánico - mantené presionado 1,5 segundos"
        >
          {sending ? <Loader2 size={28} className="panic-spin" /> : success ? <Check size={30}/> : <AlertOctagon size={26}/>}
          {holding && !success && (
            <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", transform: "rotate(-90deg)" }} viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="16" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="2.5"/>
              <circle cx="18" cy="18" r="16" fill="none" stroke="white" strokeWidth="2.5"
                      strokeDasharray={`${progress} 100`} strokeLinecap="round"
                      style={{ transition: "stroke-dasharray 0.05s linear" }}/>
            </svg>
          )}
          <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 3 }}>SOS</span>
        </button>
      </div>
    </>
  );
}
